import { create } from 'zustand';
import { apiClient } from '@/services/api';
import { authTokens } from '@/services/authTokens';
import { queryClient } from '@/services/queryClient';

// JWT payload helper — sem libs extras, parse manual de base64url
function parseJwt(token: string): any | null {
  try {
    const base64 = token.split('.')[1]
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

export interface AuthUser {
  id: string;
  email: string;
  fullName?: string;
}

export interface ImpersonatedBy {
  kind: 'master' | 'partner';
  name: string;
}

interface AuthState {
  user: AuthUser | null;
  tenantId: number | null;
  role: string | null;
  isMasterAdmin: boolean;
  partnerId: number | null;
  isPartner: boolean;
  impersonatedBy: ImpersonatedBy | null;
  loading: boolean;

  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  initialize: () => Promise<void>;
  /** Hidrata o store a partir dos tokens (usa-se após accept-invite/reset). */
  hydrateFromTokens: () => void;
  /** Instala token de impersonação e hidrata como o cliente-alvo. */
  startImpersonation: (accessToken: string, expiresAt: string) => void;
  /** Volta pra sessão do operador (master/parceiro). Retorna true se restaurou. */
  stopImpersonation: () => boolean;
}

const EMPTY = { user: null as AuthUser | null, tenantId: null, role: null, isMasterAdmin: false, partnerId: null, isPartner: false, impersonatedBy: null as ImpersonatedBy | null };

function extractFromToken(token: string | null) {
  if (!token) return { ...EMPTY };
  const payload = parseJwt(token);
  if (!payload) return { ...EMPTY };

  let appMeta: any = payload.app_metadata;
  if (typeof appMeta === 'string') {
    try { appMeta = JSON.parse(appMeta); } catch { appMeta = {}; }
  }
  appMeta = appMeta ?? {};

  const partnerId = appMeta.partner_id != null ? Number(appMeta.partner_id) : null;
  const imp = appMeta.impersonated_by;
  return {
    user: {
      id: payload.sub,
      email: payload.email ?? '',
    } as AuthUser,
    tenantId: appMeta.tenant_id != null ? Number(appMeta.tenant_id) : null,
    role: appMeta.role ?? null,
    isMasterAdmin: appMeta.is_master_admin === true,
    partnerId,
    isPartner: appMeta.role === 'partner' || partnerId != null,
    impersonatedBy: imp && imp.kind ? { kind: imp.kind, name: imp.name ?? '' } as ImpersonatedBy : null,
  };
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  tenantId: null,
  role: null,
  isMasterAdmin: false,
  partnerId: null,
  isPartner: false,
  impersonatedBy: null,
  loading: true,

  signIn: async (email, password) => {
    set({ loading: true });
    try {
      const resp = await apiClient.login(email, password);
      const data = resp.data ?? {};
      authTokens.setTokens({
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiresAt: data.expires_at,
        refreshExpiresAt: data.refresh_expires_at,
      });
      // Zera qualquer dado cacheado de uma sessão anterior antes de hidratar a nova.
      queryClient.clear();
      const info = extractFromToken(data.access_token);
      set({ ...info, loading: false });
      return { error: null };
    } catch (err: any) {
      set({ loading: false });
      const code = err?.response?.data?.error;
      if (code === 'invalid_credentials') return { error: 'Email ou senha inválidos' };
      if (code === 'user_blocked') return { error: 'Usuário bloqueado. Contate o administrador da conta.' };
      if (code === 'user_inactive') return { error: 'Conta ainda não ativada — aceite o convite que você recebeu por email.' };
      return { error: 'Falha ao entrar. Tente novamente.' };
    }
  },

  signOut: async () => {
    const rt = authTokens.getRefreshToken();
    try { await apiClient.logout(rt); } catch { /* ignora */ }
    authTokens.clear();
    queryClient.clear();
    set({ ...EMPTY });
  },

  startImpersonation: (accessToken, expiresAt) => {
    authTokens.beginImpersonation(accessToken, expiresAt);
    // CRÍTICO: limpa o cache antes de hidratar como o cliente-alvo, senão o
    // react-query serve os dados do operador (ex.: TVs de todos os tenants
    // que o master enxerga) por até staleTime. Isolamento depende disso.
    queryClient.clear();
    set({ ...extractFromToken(accessToken), loading: false });
  },

  stopImpersonation: () => {
    const restored = authTokens.endImpersonation();
    queryClient.clear(); // descarta dados do cliente impersonado ao voltar
    set({ ...extractFromToken(authTokens.getAccessToken()), loading: false });
    return restored;
  },

  initialize: async () => {
    const token = authTokens.getAccessToken();
    const info = extractFromToken(token);
    set({ ...info, loading: false });
  },

  hydrateFromTokens: () => {
    const token = authTokens.getAccessToken();
    set({ ...extractFromToken(token), loading: false });
  },
}));
